import { useState, useRef, useCallback } from 'react';
import { downloadPhoto, downloadPhotosAsZip } from '@/services/photoService';
import { formatFileSize } from '@/types/photo';

interface PhotoDownloadButtonProps {
  photoId: string;
  label?: string;
  className?: string;
}

export default function PhotoDownloadButton({ photoId, label = '원본 다운로드', className = '' }: PhotoDownloadButtonProps) {
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const objectUrlRef = useRef<string | null>(null);

  const handleDownload = useCallback(async () => {
    if (downloading) return;
    setDownloading(true);
    setProgress('다운로드 준비 중...');
    setError('');

    try {
      const result = await downloadPhoto(photoId);
      if (!result) {
        setError('원본 사진을 찾을 수 없습니다');
        setDownloading(false);
        return;
      }

      setProgress('다운로드 중...');

      // Revoke previous object URL
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }

      const url = URL.createObjectURL(result.blob);
      objectUrlRef.current = url;

      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Clean up after a short delay
      setTimeout(() => {
        if (objectUrlRef.current === url) {
          URL.revokeObjectURL(url);
          objectUrlRef.current = null;
        }
      }, 1000);

      setProgress('');
      setDownloading(false);
    } catch {
      setError('다운로드에 실패했습니다');
      setDownloading(false);
    }
  }, [photoId, downloading]);

  return (
    <div className={className}>
      {error && (
        <div className="mb-2 bg-red-50 text-red-500 text-xs px-3 py-1.5 rounded-lg text-center">
          {error}
          <button
            onClick={() => setError('')}
            className="ml-2 underline cursor-pointer"
          >
            닫기
          </button>
        </div>
      )}
      <button
        onClick={handleDownload}
        disabled={downloading}
        className="w-full py-3 bg-background-50/15 backdrop-blur-sm text-background-50 rounded-xl text-sm font-medium hover:bg-background-50/25 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-50"
        aria-label={label}
      >
        {downloading ? (
          <>
            <div className="w-4 h-4 border-2 border-background-50/60 border-t-transparent rounded-full animate-spin"></div>
            {progress}
          </>
        ) : (
          <>
            <i className="ri-download-line"></i>
            {label}
          </>
        )}
      </button>
    </div>
  );
}

// Multi-photo download button for album detail
interface MultiDownloadButtonProps {
  selectedIds: string[];
  totalSize: number;
  albumName: string;
  onComplete: () => void;
}

export function MultiDownloadButton({ selectedIds, totalSize, albumName, onComplete }: MultiDownloadButtonProps) {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');

  const handleDownload = async () => {
    if (loading || selectedIds.length === 0) return;
    setLoading(true);
    setProgress(selectedIds.length === 1 ? '다운로드 준비 중...' : 'ZIP 생성 중...');
    setError('');

    try {
      if (selectedIds.length === 1) {
        // Single file: direct download
        const result = await downloadPhoto(selectedIds[0]);
        if (!result) {
          setError('사진을 불러올 수 없습니다');
          setLoading(false);
          return;
        }
        setProgress('다운로드 중...');
        const url = URL.createObjectURL(result.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } else {
        // Multiple files: ZIP download
        const result = await downloadPhotosAsZip(selectedIds);
        if (!result) {
          setError('사진을 불러올 수 없습니다');
          setLoading(false);
          return;
        }
        setProgress('다운로드 중...');
        const url = URL.createObjectURL(result.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }

      setProgress('');
      setLoading(false);
      onComplete();
    } catch {
      setError('다운로드에 실패했습니다');
      setLoading(false);
    }
  };

  const hasSelection = selectedIds.length > 0;
  const buttonLabel = selectedIds.length === 0
    ? '사진을 선택해주세요'
    : selectedIds.length === 1
      ? '원본 다운로드'
      : `선택 사진 ZIP 다운로드 (${selectedIds.length}장)`;

  return (
    <div>
      {error && (
        <div className="mb-2 bg-red-50 text-red-500 text-xs px-3 py-1.5 rounded-lg text-center">
          {error}
          <button onClick={() => setError('')} className="ml-2 underline cursor-pointer">닫기</button>
        </div>
      )}
      <button
        onClick={handleDownload}
        disabled={loading || !hasSelection}
        className={`w-full py-3.5 rounded-xl text-sm font-bold transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed ${
          hasSelection && !loading
            ? 'bg-accent-500 text-background-50 hover:bg-accent-600'
            : 'bg-secondary-200 text-foreground-500'
        }`}
      >
        {loading ? (
          <>
            <div className="w-4 h-4 border-2 border-background-50/60 border-t-transparent rounded-full animate-spin"></div>
            {progress}
          </>
        ) : (
          <>
            <i className="ri-download-line text-base"></i>
            {buttonLabel}
          </>
        )}
      </button>
      {!loading && totalSize > 0 && hasSelection && (
        <p className="text-[11px] text-foreground-500 text-center mt-2 font-medium">
          예상 용량: {formatFileSize(totalSize)}
        </p>
      )}
    </div>
  );
}